<template>
  <form class="LOGIN-FORM" @submit.prevent="submit">
    <div v-if="showTitle" class="h4 m-b-3 text-center text-bold" selenium="login-form-title">
      {{ titleText }}
    </div>


    <div class="p-b-4 d-flex">
      <button
          @click.prevent="handleTabClick('customer')"
          type="button"
          :class="[selectedLoginTab === 'customer' ? 'HIGHLIGHT': 'LOGINTAB']">
        Customer Sign In
      </button>
      <button
          @click.prevent="handleTabClick('pro')"
          type="button"
          :class="[selectedLoginTab === 'pro' ? 'HIGHLIGHT': 'LOGINTAB']">
        Pro Sign In
      </button>
    </div>

    <slot name="banner"/>

    <div
        :class="{ 'error': validation.hasError('email') }"
        :error="validation.firstError('email')"
        class="input-container m-b-2"
        selenium="login-form-email-input-container">
      <input
          v-vk-input
          ref="email"
          v-model.trim="email"
          :disabled="loading"
          class="input"
          selenium="input"
          type="text"
          autocorrect="off"
          autocapitalize="none">
      <label class="input-label" selenium="label">Email</label>
    </div>

    <password-field
        ref="password"
        :class="{ 'error': validation.hasError('password') }"
        :error="validation.firstError('password')"
        :disabled="loading"
        v-model="password"
    />

    <special-message ref="error" type="error" class="m-b-2" selenium="login-form-error">
      We can't find the email or password that you entered.
    </special-message>
    <div class="text-right">
      <router-link
          :to="{
            name: 'forgot-password',
            query: { email, redirectTo: this.redirectTo, proLogin: this.showPro } }"
          class="link"
          selenium="login-forgot-password-link">
        Forgot your password?
      </router-link>
    </div>

    <div class="checkboxharmony m-b-2">
      <input
          v-model="remember"
          id="remember"
          selenium="remember-me-checkbox"
          type="checkbox">
      <label for="remember" selenium="remember-me-checkbox-label">
        <span class="display-block">
          <span>Keep me signed in</span><br>
          <span class="text-sm">Only check if on a private device</span>
        </span>
      </label>

    </div>

    <button
        :disabled="loading"
        type="submit"
        class="btn w-100 m-b-2"
        selenium="login-form-button">
      {{ ctaText }}
    </button>

    <button
        v-if="!showPro"
        :disabled="loading"
        type="button"
        @click.prevent="handleOneTimeCodeClick"
        class="btn-secondary w-100 m-b-2"
        selenium="login-form-button">
      Sign In with a One-Time Code
    </button>

    <slot name="footer" :email="email">
      <div v-if="!showPro" class="text-center">
        <a
            @click="howDoISignUpOpen = true"
            class="link"
        >
          How do I sign up?
        </a>
      </div>
    </slot>
    <div>
      <vk-modal v-if ="howDoISignUpOpen" @close="howDoISignUpOpen = false">
        <div class="HOW-TO-BODY bg-white">
          <vk-modal-header class="text-bold  c-depot"/>
          <vk-modal-content class="m-4 ">
            We’re excited that you’re interested in using Pro Referral for your project!<br><br>
            There’s no need to sign up for Pro Referral first to use our service.
            Simply request a service directly from
            <a class="link" href="http://www.proreferral.com/">our homepage</a>
            and we’ll connect you with local, qualified pros in your area.<br><br>
            <span class="text-bold">Already submitted a request? </span>Use one-time code login.
          </vk-modal-content>
        </div>
      </vk-modal>
    </div>

  </form>
</template>

<script>
import Storage from '@homeservices/tool-kit/storage/local-storage';
import SimpleVueValidator, { Validator } from 'simple-vue-validator';
import { GRANT_TYPE_PR } from '@homeservices/skeleton-key';
import PasswordField from '@/components/PasswordField';
import SpecialMessage from '@/components/SpecialMessage';

const REMEMBER_ME_KEY = 'PR_REMEMBER_EMAIL';

export default {
  name: 'app-login-form',
  components: {
    PasswordField,
    SpecialMessage,
  },
  mixins: [SimpleVueValidator.mixin],
  props: {
    ctaText: {
      type: String,
      default: 'Sign In',
    },
    titleText: {
      type: String,
      default: 'Account Sign In',
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    redirectTo: {
      type: [Object, String],
      default: null,
    },
    autoRedirect: {
      type: Boolean,
      default: true,
    },
    initialEmail: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      email: this.initialEmail || Storage.get(REMEMBER_ME_KEY) || '',
      remember: true,
      password: '',
      loading: true,
      howDoISignUpOpen: false,
      selectedLoginTab: 'customer',
    };
  },
  computed: {
    showPro () {
      return this.selectedLoginTab === 'pro';
    },
  },
  validators: {
    email (value) {
      return Validator.value(value).required();
    },
    password (value) {
      return Validator.value(value).required();
    },
  },
  mounted () {
    if (this.email) {
      this.$refs.password.focus();
    } else {
      this.$refs.email.focus();
    }
    if (this.$route.params.proLogin) {
      this.selectedLoginTab = 'pro';
    }
    if (this.$route.query.redirect) {
      const path = this.$route.query.redirect.split('/');
      if (path.includes('dashboard')) {
        this.selectedLoginTab = 'pro';
      }
    }
    if (!this.$route.query.redirect) {
      this.selectedLoginTab = 'pro';
    }
  },
  created () {
    if (this.$store.getters['user/isLoggedIn']) {
      const isPro = this.$store.getters['user/isPro'];
      const consumerDashboardOrHomePage = this.$route.params.showRequestSearchPage ? 'homepage' : 'consumer::dashboard';
      this.$router.push({
        name: isPro ? 'dashboard' : consumerDashboardOrHomePage,
      });
    } else {
      this.loading = false;
    }
  },
  methods: {
    async submit () {
      this.$refs.error.hide();
      const valid = await this.$validate();
      if (!valid) { return; }
      try {
        this.loading = true;
        await this.$store.dispatch('user/login', {
          username: this.email,
          password: this.password,
          type: GRANT_TYPE_PR,
        });
        if (this.remember) {
          Storage.set(REMEMBER_ME_KEY, this.email);
        } else {
          Storage.remove(REMEMBER_ME_KEY);
        }
        this.$events.$emit(this.$events.USER_LOGIN, { email: this.email });
        this.loading = false;
        this.email = '';
        this.password = '';
        this.validation.reset();
        this.redirect();
        this.$emit('success');
      } catch (err) {
        this.$events.$emit(
          this.$events.USER_LOGIN_FAILURE,
          {
            isProLogin: this.showPro,
            error: {
              errorDescription: err.response.data.error_description,
              status: err.response.status,
              statusText: err.response.statusText,
            },
            path: this.$route.fullPath,
          }
        );
        this.$refs.error.trigger(10000);
        this.loading = false;
        this.$emit('failure');
      }
    },
    redirect () {
      if (this.redirectTo && this.redirectTo !== '/' && this.redirectTo !== '/pro-homepage') {
        this.$router.push(this.redirectTo);
      } else if (this.autoRedirect) {
        const isPro = this.$store.getters['user/isPro'];
        const consumerDashboardOrHomePage = this.$route.params.showRequestSearchPage ? 'homepage' : 'consumer::dashboard';
        this.$router.push({
          name: isPro ? 'dashboard' : consumerDashboardOrHomePage,
        });
      }
    },
    handleTabClick (tabName) {
      this.selectedLoginTab = tabName;
      this.$events.$emit(this.$events.LOGIN_TAB_SWITCH, { newTabName: tabName });
    },
    handleOneTimeCodeClick () {
      this.$events.$emit(this.$events.OTC_LOGIN_PAGE_CLICK);
      this.$router.push({
        name: 'one-time-code', query: { email: this.email, redirectTo: this.redirectTo },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools.scss";
  @import "~@/views/Dashboard/constants";

  .LOGIN-FORM {
    @include responsive(width, 100%, 656px);
  }
  .HOW-TO-BODY {
    @include responsive(width, 100%, 656px);
     display: flex;
    flex-direction: column;
  }
  .HIGHLIGHT {
    flex: 1 1 auto;
    height: 53px;
    font-weight: 700;
    width: calc(50% -8px);
    color: $color-depot;
    border: none;
    border-bottom: solid 3px $color-depot;
    background-color: white;
    will-change: left, width;
    transition: left $transition-half-duration, width $transition-half-duration;
  }
  .LOGINTAB {
    flex: 1 1 auto;
    height: 53px;
    font-weight: 700;
    width: calc(50% -8px);
    color: $color-gray70;
    border: none;
    border-bottom: solid 3px $color-gray60;
    background-color: white;
    will-change: left, width;
    transition: left $transition-half-duration, width $transition-half-duration;
  }
</style>
